import React from 'react';
import { motion } from 'framer-motion';
import { FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const plans = [
  {
    name: 'Starter',
    price: '$750',
    per: '/month',
    tagline: 'Single-location, one workflow',
    features: [
      'Up to 1,500 AI minutes / month',
      '1 workflow',
      'Staff-first / AI-fallback setup',
      'Email or SMS handoff',
      'Call recordings + transcripts',
      'Monthly optimization review',
    ],
  },
  {
    name: 'Growth',
    price: '$1,250',
    per: '/month',
    tagline: 'Multi-department or after-hours + overflow',
    highlight: true,
    features: [
      'Up to 3,000 AI minutes / month',
      'Up to 2 workflows / departments',
      'Basic CRM or helpdesk integration',
      'Call recordings + transcripts',
      'Monthly optimization review',
      'Analytics snapshot',
    ],
  },
  {
    name: 'Core',
    price: '$2,000',
    per: '/month',
    tagline: 'Higher volume, advanced routing',
    features: [
      'Up to 6,000 AI minutes / month',
      'Up to 3 workflows / departments',
      'Advanced routing and escalation',
      'CRM / helpdesk integration',
      'Priority support',
      'Analytics + optimization',
    ],
  },
  {
    name: 'Custom',
    price: 'Contact us',
    per: '',
    tagline: 'Multi-location, complex workflows, outbound',
    features: [
      'Multi-location deployments',
      'Higher-volume capacity',
      'Advanced API integrations',
      'Complex custom workflows',
      'Outbound workflow packages',
    ],
  },
];

const Pricing = () => {
  const scrollToBooking = () => document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="pricing-section" className="py-20 border-t" style={{ background: '#0a0a0a', borderColor: '#1a1a1a' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-4">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#4ade80' }}>Pricing</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#f1f1f1' }}>Managed service pricing.</h2>
          <p className="text-base max-w-xl mb-3" style={{ color: '#888' }}>
            Implementation fee applies to all plans. Monthly plans are month-to-month after implementation.
          </p>
          <div className="inline-block px-4 py-2 rounded-lg text-sm border" style={{ background: '#111', borderColor: '#1f1f1f', color: '#888' }}>
            Implementation: <span style={{ color: '#f1f1f1' }}>$1,500 standard</span> — custom pricing for advanced or multi-department setups.
          </div>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {plans.map((plan, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="p-6 rounded-2xl border flex flex-col"
              style={{ background: plan.highlight ? '#0f2a1a' : '#111', borderColor: plan.highlight ? '#2d6a4f' : '#1f1f1f' }}>
              {plan.highlight && (
                <span className="text-xs font-bold px-2 py-1 rounded mb-4 self-start" style={{ background: '#1a3a2a', color: '#4ade80' }}>
                  Most Common
                </span>
              )}
              <h3 className="text-lg font-bold mb-1" style={{ color: '#f1f1f1' }}>{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold" style={{ color: plan.highlight ? '#4ade80' : '#f1f1f1' }}>{plan.price}</span>
                <span className="text-sm" style={{ color: '#555' }}>{plan.per}</span>
              </div>
              <p className="text-xs mb-5" style={{ color: '#555' }}>{plan.tagline}</p>
              <div className="space-y-3 flex-1 mb-6">
                {plan.features.map((f, j) => (
                  <div key={j} className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#1a3a2a' }}>
                      <SafeIcon icon={FiCheck} className="w-2.5 h-2.5" style={{ color: '#4ade80' }} />
                    </div>
                    <span className="text-xs leading-relaxed" style={{ color: '#888' }}>{f}</span>
                  </div>
                ))}
              </div>
              <button onClick={scrollToBooking}
                className="w-full py-3 rounded-lg text-sm font-semibold border transition-all"
                style={{
                  background: plan.highlight ? '#4ade80' : 'transparent',
                  color: plan.highlight ? '#0a0a0a' : '#888',
                  borderColor: plan.highlight ? '#4ade80' : '#1f1f1f',
                }}>
                {plan.name === 'Custom' ? 'Contact Us' : 'Book a Call'}
              </button>
            </motion.div>
          ))}
        </div>
        <p className="text-xs mt-6" style={{ color: '#444' }}>
          Overage: $0.25 / minute above included usage. All plans are month-to-month after implementation.
        </p>
      </div>
    </section>
  );
};

export default Pricing;